import { ChangeDetectionStrategy, Component, ErrorHandler, Injector, OnDestroy, OnInit, QueryList, ViewChildren, afterNextRender, computed, inject, signal } from '@angular/core'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'

import { FieldTree, form, required } from '@angular/forms/signals'
import { MatInput } from '@angular/material/input'

import { KirjanpidonProjekti } from '@lemon/core-firebase/model/kirjanpito-projektit'

import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { KirjanpitoService } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'

import { DebugService } from 'app/_angular/service/debug.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { IFirestoreBatch } from 'app/_jaettu-angular/base-firebase.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom, of as observableOf } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { AlikokoelmaanVientiLemonatoristaLemonaidiinTyo } from '../../_jaettu-lemonator/model/asiakas'

interface ProjektiLomake {
  avain: string
  nimi: string
  vari: string | null
  usableInLemonator: boolean | null
  viewableInLemonator: boolean | null
  viewableInLemonaid: boolean | null
  aktiivinen: boolean | null
  raporteilla: boolean | null
}

@Component({
  selector: '[app-kirjanpito-projektit-listaus]',
  templateUrl: './projektit-listaus.component.html',
  styleUrls: ['./projektit-listaus.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KirjanpitoProjektitListausComponent implements OnInit, OnDestroy {
  @ViewChildren('nimi', { read: MatInput }) nimiInputit: QueryList<MatInput>

  private _addedProjektitSubject: BehaviorSubject<FieldTree<ProjektiLomake>[]> = new BehaviorSubject([])
  projektitArrayObservable: Observable<FieldTree<ProjektiLomake>[]>

  lemonaidProjektitUriObservable: Observable<string>
  lemonaidProjektitEncodedUriObservable: Observable<string>
  lemonatorProjektitUriObservable: Observable<string>
  lemonatorProjektitEncodedUriObservable: Observable<string>
  kirjanpitajaOnDevaajaObservable: Observable<boolean>

  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  private readonly _projektiLomakkeet = signal<FieldTree<ProjektiLomake>[]>([])
  readonly projektienArvot = computed(() => this._projektiLomakkeet().map(projektiLomake => projektiLomake().value()))

  colorNameAndClassMap: {}

  private _saveStarted: boolean
  showCommonError: boolean = false

  constructor(
    private _firebase: FirebaseLemonator,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _asiakasService: AsiakasService,
    private _kirjanpitoService: KirjanpitoService,
    private _validationService: FormValidationService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _debugService: DebugService,
    private _timestampService: TimestampService,
  ) {}

  ngOnInit() {
    this.colorNameAndClassMap = this._kirjanpitoService.getColorNameAndClassMap()
    const tietokannastaObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas) {
          return observableOf<KirjanpidonProjekti[]>([])
        }
        return this._firebase.firestoreCollection<KirjanpidonProjekti>(this._kirjanpitoUriService.annaKirjanpitoProjektitCollection(asiakas.avain)).listen()
      }),
      map(projektit => {
        const projektiLomakkeet: FieldTree<ProjektiLomake>[] = []
        if (projektit?.length) {
          projektit.sort((a, b) => {
            return (a.nimi || '').localeCompare(b.nimi || '')
          })
          for (const projekti of projektit) {
            projektiLomakkeet.push(this._luoProjektinLomake(projekti))
          }
        }
        return projektiLomakkeet
      }),
    )
    this.projektitArrayObservable = combineLatest([tietokannastaObservable, this._addedProjektitSubject]).pipe(
      map(([tietokannasta, projektit]) => {
        this._projektiLomakkeet.set([...tietokannasta, ...projektit])
        return this._projektiLomakkeet()
      }),
    )

    // Debug thingies
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable

    this.lemonaidProjektitUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return 'customers/' + avainTiedot.avain + '/projektit/'
        }
        return ''
      }),
    )
    this.lemonaidProjektitEncodedUriObservable = this.lemonaidProjektitUriObservable.pipe(map(uri => this._debugService.createFirestoreLinkLemonaid(uri)))

    this.lemonatorProjektitUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return this._kirjanpitoUriService.annaKirjanpitoProjektitCollection(avainTiedot.avain)
        }
        return ''
      }),
    )
    this.lemonatorProjektitEncodedUriObservable = this.lemonatorProjektitUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLink(uri)
      }),
    )
  }

  createNewProject() {
    const newProject: KirjanpidonProjekti = {
      avain: this._firebase.firestoreCreateId(),
      nimi: null,
      color: null,
      usableInLemonator: true,
      viewableInLemonator: true,
      viewableInLemonaid: false,
    }
    this._addedProjektitSubject.value.push(this._luoProjektinLomake(newProject))
    this._addedProjektitSubject.next(this._addedProjektitSubject.value)
    afterNextRender(
      () => {
        const last = this.nimiInputit.last
        if (last) {
          last.focus()
        }
      },
      { injector: this._injector },
    )
  }

  private _luoProjektinLomake(projekti: KirjanpidonProjekti) {
    return form(
      signal<ProjektiLomake>({
        avain: projekti.avain ?? null,
        nimi: projekti.nimi ?? '',
        vari: projekti.color ?? null,
        usableInLemonator: projekti.usableInLemonator ?? null,
        viewableInLemonator: projekti.viewableInLemonator ?? null,
        viewableInLemonaid: projekti.viewableInLemonaid ?? null,
        aktiivinen: projekti.viewableInLemonaid ?? null,
        raporteilla: projekti.usableInLemonator ?? null,
      }),
      polku => {
        required(polku.nimi)
        required(polku.vari)
      },
      { injector: this._injector },
    )
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  async saveValidProjektit() {
    if (!this._projektiLomakkeet().every(projektiLomake => projektiLomake().valid())) {
      for (const projektiLomake of this._projektiLomakkeet()) {
        projektiLomake().markAsTouched()
      }
      setTimeout(() => this._validationService.naytaEnsimmainenVirhe(), 100)
      return
    }

    if (this._saveStarted) {
      return
    }
    this._saveStarted = true
    this._ladataanService.aloitaLataaminen()

    const batch = this._firebase.firestoreBatch()
    const asiakastiedot = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    const kaikkiProjektit = this._projektiLomakkeet()

    for (const projektiLomake of kaikkiProjektit) {
      await this.addProjektiToBatch(asiakastiedot.avain, projektiLomake, batch)
    }

    // Projektien synkkaus Lemonaidiin
    const alikokoelmaSynkkaustyoDoc = 'tyojono/' + asiakastiedot.avain + '/alikokoelmat-lemontree-export/' + this._firebase.firestoreCreateId()
    const alikokoelmaSynkkaustyonTiedot: AlikokoelmaanVientiLemonatoristaLemonaidiinTyo = {
      aloitettu: this._timestampService.now(),
      tyyppi: 'projektit',
    }
    batch.set(alikokoelmaSynkkaustyoDoc, alikokoelmaSynkkaustyonTiedot)

    const toBePersisted = this._addedProjektitSubject.value
    this._addedProjektitSubject.next([])
    return batch
      .commit()
      .then(() => {
        this._addedProjektitSubject.value.length = 0
        this._addedProjektitSubject.next(this._addedProjektitSubject.value)
      })
      .catch(error => {
        this._addedProjektitSubject.next(toBePersisted)
        this.showCommonError = true
        this._errorHandler.handleError(new Error('Projektit save failed ' + error?.toString()))
      })
      .finally(() => {
        this._saveStarted = false
        this._ladataanService.lopetaLataaminen()
      })
  }

  private async addProjektiToBatch(asiakasAvain: string, projektiLomake: FieldTree<ProjektiLomake>, batch: IFirestoreBatch) {
    const avain = projektiLomake.avain().value()
    const doc = this._kirjanpitoUriService.annaKirjanpitoProjektinUri(asiakasAvain, avain)
    const data: KirjanpidonProjekti = {
      avain: avain,
      nimi: projektiLomake.nimi().value(),
      color: projektiLomake.vari().value(),
      usableInLemonator: projektiLomake.usableInLemonator().value(),
      viewableInLemonator: projektiLomake.viewableInLemonator().value(),
      viewableInLemonaid: projektiLomake.viewableInLemonaid().value(),
    }
    batch.set(doc, data)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
